import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Chip } from '@/components/ui/Chip';
import {
  VIBRATION_MODES,
  VIBRATION_MODE_LABELS,
  type VibrationMode,
} from '@/types/activity';

interface VibrationModeSelectProps {
  modes: VibrationMode[];
  currentlyAvailableModes: Set<VibrationMode>;
  selected: VibrationMode[];
  onChange: (modes: VibrationMode[]) => void;
  placeholder?: string;
}

const VIBRATION_MODE_GROUPS: Array<{ id: string; label: string; modes: VibrationMode[] }> = [
  {
    id: 'metronome',
    label: 'Metronome',
    modes: ['metronome', 'pink-metronome', 'adaptive-metronome'],
  },
  {
    id: 'vibrate',
    label: 'Vibrate',
    modes: [
      'ground-vibrate',
      'motion-vibrate',
      'heel-strike-vibrate',
      'load-vibrate',
      'unload-vibrate',
      'circumduction-vibrate',
      'swing-plus-vibrate',
      'swing-vibrate',
      'crescendo-vibrate',
      'heel-off-vibrate',
      'modulation-vibrate',
      'length-vibrate',
    ],
  },
  {
    id: 'other',
    label: 'Other',
    modes: ['none', 'hs-ho-feedback'],
  },
];

export function VibrationModeSelect({
  modes: _modes,
  currentlyAvailableModes: _currentlyAvailableModes,
  selected,
  onChange,
  placeholder = 'Select vibration modes...',
}: VibrationModeSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filteredGroups = useMemo(() => {
    const term = search.toLowerCase();
    return VIBRATION_MODE_GROUPS.map((group) => ({
        ...group,
        modes: group.modes.filter((mode) =>
          VIBRATION_MODE_LABELS[mode].toLowerCase().includes(term)
        ),
      })).filter((group) => !search || group.modes.length > 0);
  }, [search]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const toggleMode = (mode: VibrationMode) => {
    if (selectedSet.has(mode)) {
      onChange(selected.filter((selectedMode) => selectedMode !== mode));
      return;
    }

    onChange([...selected, mode]);
  };

  const clearAll = () => onChange([]);

  const selectAll = () => {
    if (selected.length === VIBRATION_MODES.length) {
      onChange([]);
    } else {
      onChange([...VIBRATION_MODES]);
    }
  };

  const toggleGroup = (groupModes: VibrationMode[]) => {
    const allSelected = groupModes.every((mode) => selectedSet.has(mode));
    if (allSelected) {
      onChange(selected.filter((mode) => !groupModes.includes(mode)));
    } else {
      onChange(Array.from(new Set([...selected, ...groupModes])));
    }
  };

  const displayedItems = selected.slice(0, 3);
  const remainingCount = selected.length - displayedItems.length;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          'flex min-h-[42px] w-full items-center justify-between gap-2 rounded-lg border border-border bg-white px-3 py-2 text-left text-sm transition-all',
          'hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          isOpen && 'border-primary ring-2 ring-primary/20'
        )}
      >
        <div className="flex flex-1 flex-wrap items-center gap-1.5">
          {selected.length === 0 ? (
            <span className="text-muted">{placeholder}</span>
          ) : (
            <>
              {displayedItems.map((mode) => (
                <Chip
                  key={mode}
                  size="sm"
                  variant="selected"
                  removable
                  onRemove={() => toggleMode(mode)}
                  onClick={(event) => event.stopPropagation()}
                >
                  {VIBRATION_MODE_LABELS[mode]}
                </Chip>
              ))}
              {remainingCount > 0 && (
                <span className="text-sm text-muted">+{remainingCount} more</span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          {selected.length > 0 && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(event) => {
                event.stopPropagation();
                clearAll();
              }}
              className="rounded p-1 text-muted hover:bg-gray-200 hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </span>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </div>
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-2 max-h-96 w-full overflow-hidden rounded-lg border border-border bg-white shadow-lg">
          <div className="border-b border-border p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search vibration modes..."
                className="w-full rounded-lg border border-border bg-gray-50 py-2 pl-10 pr-3 text-sm placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          <div className="flex items-center justify-between border-b border-border px-3 py-2 text-xs text-muted">
            <span>{selected.length} selected</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={selectAll}
                className="text-xs font-medium text-foreground hover:underline"
              >
                {selected.length === VIBRATION_MODES.length ? 'Clear all' : 'Select all'}
              </button>
              <button
                type="button"
                onClick={clearAll}
                className="text-xs font-medium text-foreground hover:underline"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto py-2">
            {filteredGroups.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted">
                No results found
              </div>
            ) : (
              filteredGroups.map((group) => {
                const groupSelectedCount = group.modes.filter((mode) => selectedSet.has(mode)).length;
                const groupAllSelected = groupSelectedCount === group.modes.length;

                return (
                  <div key={group.id} className="px-2 py-1">
                    <div className="flex items-center justify-between px-2 py-1.5 text-xs font-medium text-muted">
                      <span>{group.label}</span>
                      <button
                        type="button"
                        onClick={() => toggleGroup(group.modes)}
                        className="text-xs font-medium text-foreground hover:underline"
                      >
                        {groupAllSelected ? 'Clear' : 'All'} ({groupSelectedCount}/{group.modes.length})
                      </button>
                    </div>

                    <div className="space-y-1">
                      {group.modes.map((mode) => {
                        const isSelected = selectedSet.has(mode);

                        return (
                          <button
                            key={mode}
                            type="button"
                            onClick={() => toggleMode(mode)}
                            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm hover:bg-gray-50"
                          >
                            <div
                              className={cn(
                                'flex h-5 w-5 items-center justify-center rounded border transition-colors',
                                isSelected
                                  ? 'border-primary bg-white text-primary'
                                  : 'border-border bg-white text-transparent'
                              )}
                            >
                              <Check className="h-3.5 w-3.5" />
                            </div>
                            <span className="text-foreground">
                              {VIBRATION_MODE_LABELS[mode]}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
